<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import ConversationApi from 'dashboard/api/inbox/conversation';
import SearchAPI from 'dashboard/api/search';
import { forwardMessage } from 'dashboard/helper/forwardMessage';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  content: { type: String, default: '' },
  attachments: { type: Array, default: () => [] },
  inboxId: { type: Number, required: true },
});

const { t } = useI18n();
const dialogRef = ref(null);
const isLoading = ref(false);
const conversations = ref([]);
const selectedIds = ref([]);
const query = ref('');

const isSearching = computed(() => query.value.trim().length > 0);

const listTitle = computed(() =>
  isSearching.value
    ? t('CONVERSATION.FORWARD_MESSAGE.RESULTS')
    : t('CONVERSATION.FORWARD_MESSAGE.RECENT')
);

const attachmentLabel = computed(() => {
  const [attachment] = props.attachments;
  return attachment
    ? // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      t(`CHAT_LIST.ATTACHMENTS.${attachment.fileType}.CONTENT`)
    : '';
});

// The recent list and the search endpoint describe a conversation differently,
// so both are reduced to what a row needs before reaching the template.
const fetchRecentConversations = async () => {
  const {
    data: {
      data: { payload },
    },
  } = await ConversationApi.get({
    inboxId: props.inboxId,
    status: 'all',
    sortBy: 'last_activity_at_desc',
  });

  conversations.value = payload.map(
    ({ id, meta, last_non_activity_message: lastMessage }) => ({
      id,
      name: meta.sender.name,
      thumbnail: meta.sender.thumbnail,
      preview: lastMessage?.content ?? '',
    })
  );
};

const searchConversations = async () => {
  const {
    data: { payload },
  } = await SearchAPI.conversations({ q: query.value.trim() });

  conversations.value = payload.conversations
    .filter(({ inbox }) => inbox.id === props.inboxId)
    .map(({ id, contact, message }) => ({
      id,
      name: contact.name,
      thumbnail: contact.thumbnail,
      preview: message?.content ?? '',
    }));
};

const loadConversations = debounce(() => {
  if (isSearching.value) {
    searchConversations();
  } else {
    fetchRecentConversations();
  }
}, 300);

const toggleConversation = id => {
  selectedIds.value = selectedIds.value.includes(id)
    ? selectedIds.value.filter(selectedId => selectedId !== id)
    : [...selectedIds.value, id];
};

const open = async () => {
  selectedIds.value = [];
  conversations.value = [];
  query.value = '';
  dialogRef.value?.open();
  await fetchRecentConversations();
};

const close = () => {
  dialogRef.value?.close();
};

const handleConfirm = async () => {
  if (!selectedIds.value.length) return;

  isLoading.value = true;
  try {
    const { sent, failed } = await forwardMessage({
      conversationIds: selectedIds.value,
      content: props.content,
      attachments: props.attachments,
    });

    if (failed) {
      useAlert(
        t('CONVERSATION.FORWARD_MESSAGE.PARTIAL_ERROR', { sent, failed })
      );
    } else {
      useAlert(t('CONVERSATION.FORWARD_MESSAGE.SUCCESS', sent));
    }
    close();
  } catch (error) {
    useAlert(t('CONVERSATION.FORWARD_MESSAGE.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('CONVERSATION.FORWARD_MESSAGE.TITLE')"
    :confirm-button-label="
      t('CONVERSATION.FORWARD_MESSAGE.CONFIRM', selectedIds.length)
    "
    :is-loading="isLoading"
    :disable-confirm-button="!selectedIds.length"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-3">
      <Input
        v-model="query"
        :placeholder="t('CONVERSATION.FORWARD_MESSAGE.SEARCH_PLACEHOLDER')"
        class="w-full"
        @input="loadConversations"
      />
      <span class="text-sm font-medium text-n-slate-11">{{ listTitle }}</span>
      <div class="flex flex-col overflow-y-auto max-h-72 -mx-2">
        <button
          v-for="conversation in conversations"
          :key="conversation.id"
          type="button"
          class="flex items-center w-full gap-3 px-2 py-2 rounded-lg hover:bg-n-alpha-1"
          @click="toggleConversation(conversation.id)"
        >
          <Checkbox :model-value="selectedIds.includes(conversation.id)" />
          <Avatar
            :name="conversation.name"
            :src="conversation.thumbnail"
            :size="32"
            rounded-full
          />
          <div class="min-w-0 text-start">
            <div class="text-sm font-medium truncate text-n-slate-12">
              {{ conversation.name }}
            </div>
            <div class="text-xs truncate text-n-slate-11">
              {{ conversation.preview }}
            </div>
          </div>
        </button>
        <span
          v-if="!conversations.length"
          class="px-2 py-4 text-sm text-n-slate-11"
        >
          {{ t('CONVERSATION.FORWARD_MESSAGE.EMPTY_STATE') }}
        </span>
      </div>
      <div
        class="flex items-start gap-2 p-3 rounded-lg bg-n-alpha-1 text-n-slate-11"
      >
        <Icon icon="i-lucide-forward" class="size-4 flex-shrink-0 mt-0.5" />
        <div class="min-w-0 text-sm">
          <div v-if="attachmentLabel" class="font-medium text-n-slate-12">
            {{ attachmentLabel }}
          </div>
          <div class="line-clamp-2">{{ content }}</div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
